import { defaultInfoModalState } from './types';
import {
  processPhoneNumber,
  processBirthday,
  createFullName,
  processKycStatusRequest,
  processTwoFaDisabledInfo,
  processTwoFaCheckInfo,
  processTwoFaCheckedInfo,
  parseGetUserRequestData
} from './utils';
import * as AppTypes from '../action/types';
import i18n from '../../locale';
import defaultInitialState from './constants/default-state';

export default (state = defaultInitialState, action) => {
  switch (action.type) {
    case AppTypes.SET_RESPONSIVE_HEADER_VIEW:
      return {
        ...state,
        ui: {
          ...state.ui,
          responsiveHeader: action.data
        }
      };

    case AppTypes.SET_RESPONSIVE_CONTENT_VIEW:
      return {
        ...state,
        ui: {
          ...state.ui,
          responsiveContent: action.data
        }
      };

    case AppTypes.TOGGLE_INFO_MODAL:
      return {
        ...state,
        ui: {
          ...state.ui,
          infoModal: action.data
        }
      };
    case AppTypes.SET_LOADING_MODAL:
      return {
        ...state,
        ui: {
          ...state.ui,
          loadingModalVisible: action.data
        }
      };

    case AppTypes.SET_MOBILE_BROWSER:
      return {
        ...state,
        ui: {
          ...state.ui,
          isMobile: action.data.isMobile
        }
      };

    case AppTypes.SET_DEVICE_ORIENTATION:
      return {
        ...state,
        ui: {
          ...state.ui,
          orientation: action.data.orientation
        }
      };

    case AppTypes.FETCH_INIT_DATA_SUCCESS: {
      const {
        nationalities,
        countries,
        kyc,
        twoFaDataDisabled,
        twoFaDataChecked,
        verifications,
        theme,
        logo,
        backToAppUrl,
        u2fUrl,
        customFields,
        title,
        favicon,
        appType,
        modules,
        nonEditableFields,
        available2FA
      } = action.data;
      // process disabled options
      const withDisabledOpts = processTwoFaDisabledInfo(twoFaDataDisabled);
      // process checked options
      const withCheckedOpts = processTwoFaCheckedInfo(
        withDisabledOpts,
        twoFaDataChecked,
        u2fUrl
      );

      return {
        ...state,
        ui: {
          ...state.ui,
          initialLoading: false,
          logo,
          theme,
          title,
          favicon,
          appType,
          modules
        },
        user: {
          ...state.user,
          verifications,
          auth: withCheckedOpts,
          kyc: processKycStatusRequest(kyc),
          nonEditableFields,
          available2FA
        },
        backToAppUrl,
        customFields,
        nationalities,
        countries
      };
    }

    case AppTypes.UPDATE_2FA_SETTINGS_SUCCESS:
      return {
        ...state,
        user: {
          ...state.user,
          auth: processTwoFaCheckInfo(state.user.auth, action.data)
        }
      };

    case AppTypes.CHANGE_EMAIL_REQUEST:
    case AppTypes.VERIFY_EMAIL_CODE_REQUEST: {
      return {
        ...state,
        ui: {
          ...state.ui,
          emailChangeProcess: {
            ...state.ui.emailChangeProcess,
            apiCallState: 'start'
          }
        }
      };
    }

    case AppTypes.CHANGE_EMAIL_SUCCESS:
    case AppTypes.VERIFY_EMAIL_CODE_SUCCESS: {
      return {
        ...state,
        ui: {
          ...state.ui,
          emailChangeProcess: {
            apiCallState: 'finish'
          }
        }
      };
    }

    case AppTypes.CHANGE_EMAIL_FAILURE:
    case AppTypes.VERIFY_EMAIL_CODE_FAILURE: {
      return {
        ...state,
        ui: {
          ...state.ui,
          emailChangeProcess: {
            apiCallState: 'finish'
          }
        }
      };
    }

    case AppTypes.UPDATE_EMAIL_ADDRESS: {
      return {
        ...state,
        user: {
          ...state.user,
          email: action.data
        }
      };
    }

    case AppTypes.CHANGE_PHONE_NUMBER_SUCCESS:
      return {
        ...state,
        ui: {
          ...state.ui,
          phoneChangeProcess: {
            ...state.ui.phoneChangeProcess,
            apiCallState: 'finish'
          }
        },
        user: {
          ...state.user,
          ...parseGetUserRequestData(action.data.response.user)
        }
      };

    case AppTypes.UPDATE_USER:
      return {
        ...state,
        user: {
          ...state.user,
          ...action.data
        }
      };

    case AppTypes.CHANGE_PHONE_NUMBER_FAILURE:
      return {
        ...state,
        ui: {
          ...state.ui,
          phoneChangeProcess: {
            ...state.ui.phoneChangeProcess,
            apiCallState: 'finish'
          },
          infoModal: {
            ...defaultInfoModalState,
            isOpen: true,
            type: 'error',
            message: i18n.t('pages:profile.phone.modal.1.failedChange'),
            callback: {
              fnc: null,
              btnText: null
            }
          }
        }
      };

    case AppTypes.VERIFY_PHONE_NUMBER_CODE_REQUEST:
    case AppTypes.CHANGE_PHONE_NUMBER_REQUEST: {
      return {
        ...state,
        ui: {
          ...state.ui,
          phoneChangeProcess: {
            ...state.ui.phoneChangeProcess,
            apiCallState: 'start'
          }
        }
      };
    }

    case AppTypes.VERIFY_PHONE_NUMBER_CODE_SUCCESS: {
      return {
        ...state,
        ui: {
          ...state.ui,
          phoneChangeProcess: {
            ...state.ui.phoneChangeProcess,
            apiCallState: 'finish'
          }
        }
      };
    }

    case AppTypes.VERIFY_PHONE_NUMBER_CODE_FAILURE: {
      return {
        ...state,
        ui: {
          ...state.ui,
          phoneChangeProcess: {
            ...state.ui.phoneChangeProcess,
            apiCallState: 'finish'
          }
        }
      };
    }

    case AppTypes.UPDATE_USER_PROFILE_SUCCESS:
      return {
        ...state,
        ui: {
          ...state.ui,
          infoModal: action.data.infoModal
        },
        user: {
          ...state.user,
          ...parseGetUserRequestData(action.data.response)
        }
      };

    case AppTypes.UPDATE_USER_PASSWORD_SUCCESS:
      return {
        ...state,
        ui: {
          ...state.ui,
          infoModal: action.data.infoModal
        }
      };

    case AppTypes.SET_USER_DATA:
      const { data } = action.data;
      return {
        ...state,
        user: {
          ...state.user,
          fullName: createFullName(
            data.first_name,
            data.middle_name,
            data.last_name
          ),
          firstName: data.first_name,
          middleName: data.middle_name,
          lastName: data.last_name,
          nationality: data.nationality,
          email: data.email,
          username: data.username,
          phone: processPhoneNumber(data.phone_number),
          address: {
            street: data.address,
            city: data.city,
            country: data.country_code,
            zip: data.post_code
          },
          birthday: processBirthday(data.date_of_birth),
          refs: data.refs,
          pep: data.pep
        }
      };

    case AppTypes.FETCH_KYC_STATUS_SUCCESS:
      return {
        ...state,
        user: {
          ...state.user,
          kyc: {
            ...state.user.kyc,
            ...processKycStatusRequest(action.data)
          }
        }
      };

    case AppTypes.CANCEL_KYC_VERIFICATION_SUCCESS:
      return {
        ...state,
        user: {
          ...state.user,
          kyc: defaultInitialState.user.kyc
        }
      };

    case AppTypes.UPLOAD_KYC_DOCUMENT_SUCCESS:
      return {
        ...state,
        ui: {
          ...state.ui,
          infoModal: {
            ...defaultInfoModalState,
            isOpen: true,
            type: 'success',
            message: i18n.t('pages:kyc.sucessModal.heading')
          }
        },
        user: {
          ...state.user,
          kyc: {
            ...state.user.kyc,
            ...action.data
          }
        }
      };

    case AppTypes.DELETE_KYC_DOCUMENT_SUCCESS:
      return {
        ...state,
        user: {
          ...state.user,
          kyc: {
            ...state.user.kyc,
            ...action.data
          }
        }
      };

    case AppTypes.SET_CAMERA_STREAM_SUCCESS:
      return {
        ...state,
        devices: {
          ...state.devices,
          camera: {
            ...state.devices.camera,
            stream: action.data
          }
        }
      };

    case AppTypes.ENUMERATE_DEVICES_SUCCESS:
      return {
        ...state,
        devices: {
          ...state.devices,
          camera: {
            ...state.devices.camera,
            inputs: action.data
          }
        }
      };

    case AppTypes.FETCH_INIT_DATA_FAILURE:
    case AppTypes.UPDATE_USER_PASSWORD_FAILURE:
    case AppTypes.UPDATE_USER_PROFILE_FAILURE: {
      const { infoModal } = action.data;
      return {
        ...state,
        ui: {
          ...state.ui,
          infoModal
        }
      };
    }
    default:
      return state;
  }
};
